/**
 * My Workspace - Personal HR & Support Hub
 * Layer 2 of the 2-Layer Dashboard Model
 * 
 * Premium UI aligned with TitleFlow Orders dashboard experience
 */

import { useInShell } from '../shared/ModuleShell';
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  Ticket, 
  ChevronLeft,
  User,
  ChevronRight,
  Target
} from 'lucide-react';

import LeaveApplication from './LeaveApplication';
import MyLeaves from './MyLeaves';
import LeaveCalendar from './LeaveCalendar';
import LeaveApproval from './LeaveApproval';
import TeamAvailability from './TeamAvailability';
import HolidayCalendarManager from './HolidayCalendarManager';
import LeavePolicyManager from './LeavePolicyManager';
import LeaveReports from './LeaveReports';
import BalanceAdjustments from './BalanceAdjustments';
import ITTickets from './ITTickets';
import PerformanceTab from './PerformanceTab';
import { SystemDowntimeManagement } from '../session';
import { ShieldCheck, UserCircle2 } from 'lucide-react';
import MyPolicies from './MyPolicies';
import { LeaveHeader } from './leave/LeaveHeader';
import { LeaveSubTabs } from './leave/LeaveSubTabs';
import { ITSupportHeader } from './it-support/ITSupportUi';
import './TeamManagementDark.css';

// Shared with the premium module sidebar (ModuleShell) — same role rules as the in-page tabs
export const getVisibleWorkspaceTabs = (user) => WORKSPACE_TABS.filter(tab =>
  !(tab.excludeRoles && tab.excludeRoles.includes(user?.role)) && !(tab.roles && !tab.roles.includes(user?.role)));

export const WORKSPACE_TABS = [
  // WORKSPACE-LANDING-001 (Sep 2026): employee Attendance viewer retired; /dashboard/workspace now lands on a tile grid.
  // ADMIN-WS-001 (Sep 2026): Admin has no personal production scorecard — management views live in Analytics.
  { id: 'performance', label: 'Performance', icon: Target, description: 'Track your progress', roles: ['processor', 'tax_processor', 'search_processor', 'title_search_processor', 'typing_processor', 'team_leader', 'tax_search_team_leader', 'title_search_team_leader'] },
  { id: 'leave', label: 'Leave', icon: Calendar, description: 'Apply & view leaves' },
  { id: 'tickets', label: 'IT Support', icon: Ticket, description: 'Create & track tickets', excludeRoles: ['it_manager', 'it_support'] },
  { id: 'company-policies', label: 'Policies', icon: ShieldCheck, description: 'Company policies & SOPs' }
];

const APPROVER_ROLES = ['admin', 'hr_manager', 'team_leader', 'tax_search_team_leader', 'title_search_team_leader', 'it_manager'];
const HR_ROLES = ['admin', 'hr_manager'];
// HR-PERSONA-001 (Jul 2026): hr_specialist gets READ-ONLY leave admin views.
const HR_VIEW_ROLES = ['admin', 'hr_manager', 'hr_specialist'];

const LEAVE_SUBTABS = [
  { id: 'apply', label: 'Apply Leave' },
  { id: 'my-leaves', label: 'My Leaves' },
  { id: 'calendar', label: 'Calendar' },
  // LEAVE-REGISTRY P2-P4 (Jun 2026)
  // TEAM-SCOPE-003 (Jun 2026): managerial approvals live ONLY in Team
  // Management for TL/HR/IT roles — My Workspace keeps Approvals for admin only
  { id: 'approvals', label: 'Approvals', roles: ['admin'] },
  { id: 'availability', label: 'Availability', roles: APPROVER_ROLES },
  // HOLIDAY-VISIBILITY-001 (Jun 2026): visible to ALL employees (read-only
  // for non-HR roles — holidays affect everyone's leave day calculations).
  { id: 'holidays', label: 'Holidays' },
  { id: 'policies', label: 'Policies', roles: HR_ROLES },
  { id: 'reports', label: 'Reports', roles: HR_VIEW_ROLES },
  { id: 'adjustments', label: 'Adjustments', roles: HR_VIEW_ROLES }
];

// LEAVE-REGISTRY-STANDALONE (Jul 22, 2026): admin/HR roles manage leave
// configuration in the standalone Leave Registry (/admin/hr/leave-registry).
// HR-CLEANUP-001 (Jul 2026): Approvals + Availability restored HERE for
// Admin/HR Manager (registry now holds only Holidays/Policies/Reports/
// Adjustments). Team leads, IT manager and employees are unchanged.
const LEAVE_REGISTRY_ROLES = ['admin', 'hr_manager', 'hr_specialist'];
const LEAVE_ADMIN_SUBTAB_IDS = ['policies', 'reports', 'adjustments'];

/**
 * MyWorkspace
 * @param {object} currentUser — logged-in user.
 * @param {function} onNavigateBack — back-button handler (top-level mode only).
 * @param {boolean} embedded — May 17, 2026. When true, MyWorkspace is being
 *   rendered as a nested child of AdminLayout (IT Operations Center /
 *   Jurisdiction Portal). The outer page header (back nav + center title +
 *   user chip) is suppressed because AdminLayout already provides its own
 *   header, and the `min-h-screen` wrapper is dropped since AdminLayout owns
 *   the viewport scroll container. Fixes "My Workspace UI Theme Consistency"
 *   ticket — clicking My Workspace from dark portals no longer kicks the
 *   user out of their portal shell into a disjointed full-screen layout.
 * @param {boolean} darkSkin — WORKSPACE-SHELL-001 (Jun 2026). Applies the
 *   scoped `.tm-dark` IT-portal navy skin (TeamManagementDark.css) so IT
 *   roles get a fully consistent dark experience. Visual only.
 */
const MyWorkspace = ({ currentUser, onNavigateBack, embedded: embeddedProp = false, darkSkin = false }) => {
  const inShell = useInShell();
  const embedded = embeddedProp || inShell; // UNIFIED-SHELL-001: shell already renders header + sidebar nav
  // HR-NAV-CLEANUP-001 (Sep 2026): HR Portal (AdminLayout) sidebar now lists Leave / IT Support / Policies,
  // so the white internal "Navigation" card is redundant for hr_manager / hr_specialist / hr.
  const hideInternalNav = inShell || (embedded && ['hr_manager', 'hr_specialist', 'hr'].includes(currentUser?.role));
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // LEAVE-NOTIF-NAV (May 4, 2026) — deep-link support for the
  // processor's `leave_approved` / `leave_rejected` bell notifications.
  // The notification's action_url is `/dashboard/workspace?tab=my-leaves`,
  // but `my-leaves` is a *sub-tab* of the top-level `leave` tab. Without
  // this resolver the URL falls through to the default `attendance`
  // tab and the user can't see their approved leave. We accept both:
  //   • ?tab=<top-level-id>            (direct tab selection)
  //   • ?tab=<leave-subtab-id>         (auto-routes to `leave` + opens subtab)
  //   • ?tab=leave&subtab=<subtab-id>  (explicit subtab)
  const LEAVE_SUBTAB_IDS = LEAVE_SUBTABS.map(s => s.id);

  // Filter tabs based on user role
  const visibleTabs = WORKSPACE_TABS.filter(tab => {
    if (tab.excludeRoles && tab.excludeRoles.includes(currentUser?.role)) {
      return false;
    }
    if (tab.roles && !tab.roles.includes(currentUser?.role)) {
      return false;
    }
    return true;
  });
  // WORKSPACE-NAV-002 (Sep 2026): no landing page — /dashboard/workspace opens the first authorized section
  const firstTab = visibleTabs[0]?.id;

  const getInitialTab = () => {
    const tabParam = searchParams.get('tab');
    if (tabParam && visibleTabs.some(t => t.id === tabParam)) {
      return tabParam;
    }
    if (tabParam && LEAVE_SUBTAB_IDS.includes(tabParam)) {
      return 'leave';
    }
    return firstTab;
  };

  const getInitialSubtab = () => {
    const subtabParam = searchParams.get('subtab');
    if (subtabParam && LEAVE_SUBTAB_IDS.includes(subtabParam)) {
      return subtabParam;
    }
    const tabParam = searchParams.get('tab');
    if (tabParam && LEAVE_SUBTAB_IDS.includes(tabParam)) {
      return tabParam;
    }
    return 'apply';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [leaveSubTab, setLeaveSubTab] = useState(getInitialSubtab);
  const [showApplyModal, setShowApplyModal] = useState(false);

  // LEAVE-REGISTRY-STANDALONE: admin/HR see only self-service tabs here
  const visibleLeaveSubtabs = LEAVE_SUBTABS.filter(s =>
    (!s.roles || s.roles.includes(currentUser?.role)) &&
    !(LEAVE_REGISTRY_ROLES.includes(currentUser?.role) && LEAVE_ADMIN_SUBTAB_IDS.includes(s.id))
  );

  // Deep-links to admin subtabs (e.g. ?tab=approvals) fall back to Apply
  useEffect(() => {
    if (!visibleLeaveSubtabs.some(s => s.id === leaveSubTab)) {
      setLeaveSubTab('apply');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaveSubTab, currentUser?.role]);

  // Update URL when tab changes
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  const handleLeaveApplied = () => {
    setShowApplyModal(false);
    setLeaveSubTab('my-leaves');
  };

  // Sync tab with URL parameter changes (covers in-app navigation
  // from the bell while MyWorkspace is already mounted).
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    // ADMIN-WS-001: Admin deep link to the personal scorecard → management view (Productivity Analytics)
    if (tabParam === 'performance' && currentUser?.role === 'admin') {
      navigate('/analytics/productivity', { replace: true });
      return;
    }
    // Missing / unknown / not-authorized tab (incl. legacy ?tab=attendance) → canonicalize URL to the first authorized section
    if (!LEAVE_SUBTAB_IDS.includes(tabParam) && !visibleTabs.some(t => t.id === tabParam)) {
      if (firstTab && tabParam !== firstTab) setSearchParams({ tab: firstTab }, { replace: true });
      return;
    }
    // Direct top-level match
    if (visibleTabs.some(t => t.id === tabParam) && tabParam !== activeTab) {
      setActiveTab(tabParam);
      return;
    }
    // Leave sub-tab deep link
    if (LEAVE_SUBTAB_IDS.includes(tabParam)) {
      if (activeTab !== 'leave') setActiveTab('leave');
      if (leaveSubTab !== tabParam) setLeaveSubTab(tabParam);
    }
  }, [searchParams]);

  // Role guard: a tab hidden for this role renders the first authorized section
  const currentTab = visibleTabs.some(t => t.id === activeTab) ? activeTab : firstTab;
  const currentTabLabel = visibleTabs.find(t => t.id === currentTab)?.label;
  // IT-MANAGER-MY-WORKSPACE-UI-001: embedded (portal) mode uses a compact horizontal workspace
  // nav instead of a second vertical sidebar; the Dashboard shell keeps its existing card.
  const horizontalNav = embedded && !hideInternalNav;

  return (
    <div className={inShell ? '' : embedded ? (darkSkin ? 'tm-dark min-h-full pb-4' : 'ws-embedded min-h-full bg-slate-50 pb-8') : 'min-h-screen bg-slate-50'} data-testid="my-workspace-root">
      {/* Page Header — sits below main app header, not sticky.
          May 17, 2026: suppressed in embedded mode (AdminLayout already
          renders its own header + sidebar). */}
      {inShell && currentTab !== 'performance' && currentTab !== 'leave' && currentTab !== 'tickets' && (
        <div className="flex items-center gap-3 px-1 pt-1 pb-3" data-testid="workspace-shell-header">
          <div className="h-11 w-11 shrink-0 rounded-xl bg-gradient-to-br from-blue-50 to-violet-50 text-blue-600 flex items-center justify-center border border-blue-100/70 shadow-sm"><UserCircle2 className="h-5 w-5" /></div>
          <div><h1 className="text-[22px] font-bold text-slate-900 tracking-tight leading-tight">My Workspace</h1><p className="text-[12.5px] text-slate-500">Personal HR & Support Hub</p></div>
        </div>
      )}
      {horizontalNav && (
        <div className="px-5 lg:px-8 pt-5 flex flex-wrap items-center gap-3" data-testid="workspace-embedded-nav">
          <nav className="flex items-center gap-1.5 text-[12.5px] text-slate-500 mr-auto" aria-label="Breadcrumb" data-testid="workspace-breadcrumb">
            <UserCircle2 className="h-4 w-4 text-slate-400" aria-hidden="true" />
            <span className="font-medium">My Workspace</span>
            {currentTabLabel && (<><ChevronRight className="h-3.5 w-3.5 text-slate-300" aria-hidden="true" /><span className="font-semibold text-slate-800">{currentTabLabel}</span></>)}
          </nav>
          <div className="inline-flex items-center gap-1 rounded-xl bg-white border border-slate-200 shadow-sm p-1" role="tablist" data-testid="workspace-nav-tabs">
            {visibleTabs.map(tab => {
              const Icon = tab.icon;
              const isActive = currentTab === tab.id;
              return (
                <button key={tab.id} type="button" role="tab" aria-selected={isActive} onClick={() => handleTabChange(tab.id)}
                  data-testid={`workspace-nav-${tab.id}`}
                  className={`inline-flex items-center gap-2 h-9 px-3.5 rounded-lg text-[13px] font-semibold transition-[background-color,color,box-shadow] duration-200 ${
                    isActive ? 'bg-blue-600 text-white shadow-sm shadow-blue-600/25' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}>
                  <Icon className={`h-4 w-4 ${isActive ? 'text-white' : 'text-slate-400'}`} aria-hidden="true" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {!embedded && (
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left: Back Navigation */}
            <button
              onClick={onNavigateBack}
              className="flex items-center gap-2 px-3 py-1.5 -ml-3 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="text-sm font-medium">Dashboard</span>
            </button>
            
            {/* Center: Title */}
            <div className="text-center">
              <h1 className="text-lg font-bold text-slate-900 tracking-tight">My Workspace</h1>
              <p className="text-xs text-slate-500 font-medium">Personal HR & Support Hub</p>
            </div>

            {/* Right: User Info */}
            <div className="flex items-center gap-3">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-semibold text-slate-900">{currentUser?.full_name || 'User'}</p>
                <p className="text-xs text-slate-500 capitalize">{currentUser?.role?.replace(/_/g, ' ') || 'Employee'}</p>
              </div>
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm">
                <User className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      <div className={inShell ? 'px-4 lg:px-6 py-2' : embedded ? 'px-5 lg:px-8 pt-4 pb-4' : 'max-w-7xl mx-auto px-6 lg:px-8 py-8'}>
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Sidebar Navigation - Premium Card */}
          <div className={`w-full lg:w-72 flex-shrink-0 ${(hideInternalNav || horizontalNav) ? "hidden" : ""}`} data-testid="internal-nav-panel">
            <nav className={`bg-white rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden static lg:sticky ${embedded ? 'lg:top-4' : 'lg:top-20'}`}>
              {/* Nav Header */}
              <div className="px-5 py-4 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Navigation</p>
              </div>
              
              {/* Nav Items — grid on mobile, vertical list on desktop */}
              <div className="p-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-1 gap-1 lg:gap-0 lg:space-y-1">
                {visibleTabs.map(tab => {
                  const Icon = tab.icon;
                  const isActive = currentTab === tab.id;
                  
                  return (
                    <button
                      key={tab.id}
                      onClick={() => handleTabChange(tab.id)}
                      className={`w-full flex items-center gap-3 px-3 lg:px-4 py-2.5 lg:py-3.5 rounded-xl text-left transition-all duration-200 ${
                        isActive
                          ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/25'
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-lg hidden lg:flex items-center justify-center ${
                        isActive ? 'bg-white/20' : 'bg-slate-100'
                      }`}>
                        <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-slate-500'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold truncate ${isActive ? 'text-white' : 'text-slate-900'}`}>
                          {tab.label}
                        </p>
                        <p className={`text-xs hidden lg:block ${isActive ? 'text-blue-100' : 'text-slate-400'}`}>
                          {tab.description}
                        </p>
                      </div>
                      {isActive && (
                        <ChevronRight className="w-4 h-4 text-white/70 hidden lg:block" />
                      )}
                    </button>
                  );
                })}
              </div>
            </nav>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            {/* Performance Tab — premium scorecard renders its own header (PERF-STD-001) */}
            {currentTab === 'performance' && (
              <PerformanceTab currentUser={currentUser} />
            )}

            {/* Leave Tab — LEAVE-UI-001 premium presentation (sub-tab visibility rules unchanged) */}
            {currentTab === 'leave' && (
              <div className="relative isolate space-y-3.5" data-testid="leave-management-root">
                <LeaveHeader />
                <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden !mt-0" aria-hidden="true">
                  <div className="absolute top-6 left-10 h-64 w-64 rounded-full bg-blue-200/25 blur-3xl" />
                  <div className="absolute top-48 right-16 h-72 w-72 rounded-full bg-violet-200/25 blur-3xl" />
                  <div className="absolute bottom-24 left-1/3 h-64 w-64 rounded-full bg-indigo-100/35 blur-3xl" />
                </div>
                <LeaveSubTabs tabs={visibleLeaveSubtabs} active={leaveSubTab} onChange={setLeaveSubTab} />

                {leaveSubTab === 'apply' ? (
                  <LeaveApplication onSuccess={handleLeaveApplied} onPlanBetter={() => setLeaveSubTab('calendar')} onOpenPolicy={() => handleTabChange('company-policies')} />
                ) : (
                  <div className="lv-card an-rise p-5" style={{ animationDelay: '120ms' }} data-testid="leave-subtab-panel">
                    {leaveSubTab === 'my-leaves' && (
                      <MyLeaves />
                    )}
                    {leaveSubTab === 'calendar' && (
                      <LeaveCalendar />
                    )}
                    {leaveSubTab === 'approvals' && visibleLeaveSubtabs.some(s => s.id === 'approvals') && (
                      <LeaveApproval currentUser={currentUser} />
                    )}
                    {leaveSubTab === 'availability' && visibleLeaveSubtabs.some(s => s.id === 'availability') && (
                      <TeamAvailability />
                    )}
                    {leaveSubTab === 'holidays' && visibleLeaveSubtabs.some(s => s.id === 'holidays') && (
                      <HolidayCalendarManager readOnly={!HR_ROLES.includes(currentUser?.role)} />
                    )}
                    {leaveSubTab === 'policies' && visibleLeaveSubtabs.some(s => s.id === 'policies') && (
                      <LeavePolicyManager />
                    )}
                    {leaveSubTab === 'reports' && visibleLeaveSubtabs.some(s => s.id === 'reports') && (
                      <LeaveReports />
                    )}
                    {leaveSubTab === 'adjustments' && visibleLeaveSubtabs.some(s => s.id === 'adjustments') && (
                      <BalanceAdjustments />
                    )}
                  </div>
                )}
              </div>
            )}

            {/* IT Support Tab */}
            {currentTab === 'tickets' && (
              <div className="space-y-3.5">
                {/* System Downtime Management - Only for IT/Admin */}
                {['admin', 'it', 'it_support', 'it_team'].includes(currentUser?.role) && (
                  <SystemDowntimeManagement currentUser={currentUser} />
                )}
                
                {/* IT-SUPPORT-UI-001: compact premium header (replaces the solid-orange banner) */}
                <div className="relative isolate space-y-3.5" data-testid="it-support-page">
                  <ITSupportHeader />
                  <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden !mt-0" aria-hidden="true">
                    <div className="absolute top-6 left-10 h-64 w-64 rounded-full bg-orange-100/40 blur-3xl" />
                    <div className="absolute top-56 right-16 h-72 w-72 rounded-full bg-violet-200/25 blur-3xl" />
                  </div>
                  <ITTickets currentUser={currentUser} />
                </div>
              </div>
            )}


            {/* Company Policies Tab (POLICY-MGMT-001, Jun 2026) */}
            {currentTab === 'company-policies' && (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden">
                  <div className="px-8 py-6 bg-gradient-to-r from-blue-700 to-indigo-700">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                        <ShieldCheck className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-white">Company Policies</h2>
                        <p className="text-blue-100 mt-0.5">Read and acknowledge policies assigned to you</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <MyPolicies />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyWorkspace;
